import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../api/ApiClientContext";
import { deleteReleaseImage, fetchRelease, uploadReleaseImage } from "../api/catalog-api";
import { ApiError, userFacingMessage } from "../api/errors";
import type { PublicRelease } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useCatalog } from "../catalog/CatalogContext";
import { useCollection } from "../collection/CollectionContext";
import { Button } from "../components/Button";
import { CollectionQuantityControls } from "../components/CollectionQuantityControls";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator";
import {
  ReleaseImageGallery,
  useSelectedReleaseImage,
} from "../components/ReleaseImageGallery";
import { ReleasePhotoUpload } from "../components/ReleasePhotoUpload";
import { ReleaseMeta } from "../components/ReleaseMeta";

export function ReleaseDetailPage() {
  const client = useApiClient();
  const navigate = useNavigate();
  const { releaseId = "" } = useParams();
  const auth = useAuth();
  const catalog = useCatalog();
  const collection = useCollection();
  const [release, setRelease] = useState<PublicRelease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { selectedImageId, setSelectedImageId } = useSelectedReleaseImage(release);
  const canUpload =
    auth.status === "authenticated" && auth.account.isDemo === false;

  const [replaceTargetImageId, setReplaceTargetImageId] = useState<
    string | null
  >(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replacePreviewUrl, setReplacePreviewUrl] = useState<string | null>(null);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [replaceError, setReplaceError] = useState<string | null>(null);

  function clearReplace() {
    if (replacePreviewUrl) {
      URL.revokeObjectURL(replacePreviewUrl);
    }
    setReplaceTargetImageId(null);
    setReplaceFile(null);
    setReplacePreviewUrl(null);
    setReplaceBusy(false);
    setReplaceError(null);
    if (replaceInputRef.current) {
      replaceInputRef.current.value = "";
    }
  }

  function startReplace(imageId: string) {
    if (!canUpload) {
      return;
    }
    setReplaceError(null);
    setReplaceFile(null);
    if (replacePreviewUrl) {
      URL.revokeObjectURL(replacePreviewUrl);
    }
    setReplacePreviewUrl(null);
    setReplaceTargetImageId(imageId);
    replaceInputRef.current?.click();
  }

  async function replace() {
    if (!release || !replaceTargetImageId || !replaceFile || replaceBusy) {
      return;
    }

    const oldImageId = replaceTargetImageId;
    const previousIds = new Set((release.images ?? []).map((img) => img.imageId));

    setReplaceBusy(true);
    setReplaceError(null);

    try {
      const afterUpload = await uploadReleaseImage(
        client,
        release.releaseId,
        replaceFile,
      );

      // New photo is uploaded, so show it even if deletion fails.
      setRelease(afterUpload);
      catalog.patchRelease(afterUpload);
      collection.patchRelease(afterUpload);

      const uploadedDifferent =
        afterUpload.images?.find(
          (img) =>
            img.source === "USER_UPLOAD" &&
            img.ownedByCurrentUser &&
            !previousIds.has(img.imageId),
        ) ?? null;

      const newImageId = uploadedDifferent?.imageId ?? null;
      if (newImageId && newImageId !== oldImageId) {
        const afterDelete = await deleteReleaseImage(
          client,
          release.releaseId,
          oldImageId,
        );
        setRelease(afterDelete);
        catalog.patchRelease(afterDelete);
        collection.patchRelease(afterDelete);
        setSelectedImageId(newImageId);
      } else if (newImageId) {
        setSelectedImageId(newImageId);
      }

      clearReplace();
    } catch (err) {
      setReplaceError(userFacingMessage(err));
      setReplaceBusy(false);
    }
  }

  async function deleteUserPhoto(imageId: string) {
    if (!release) {
      return;
    }
    setError(null);
    try {
      const updated = await deleteReleaseImage(
        client,
        release.releaseId,
        imageId,
      );
      setRelease(updated);
      catalog.patchRelease(updated);
      collection.patchRelease(updated);
    } catch (err) {
      setError(userFacingMessage(err));
    }
  }

  useEffect(() => {
    if (!releaseId) {
      setLoading(false);
      setError("Релиз не найден.");
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setRelease(null);

    void (async () => {
      try {
        const data = await fetchRelease(client, releaseId, {
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setRelease(data);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        if (err instanceof ApiError && err.code === "AUTH_REQUIRED") {
          return;
        }
        setError(userFacingMessage(err));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [client, releaseId]);

  const quantity = release
    ? collection.getQuantity(release.releaseId)
    : 0;
  const busy =
    collection.mutatingReleaseId !== null &&
    collection.mutatingReleaseId !== release?.releaseId;
  const mutatingThis =
    release !== null && collection.mutatingReleaseId === release.releaseId;

  return (
    <section className="hw-page">
      <p className="hw-back">
        <Button
          type="button"
          variant="ghost"
          className="hw-back__button"
          onClick={() => {
            if (window.history.length > 1) {
              void navigate(-1);
            } else {
              void navigate("/catalog");
            }
          }}
        >
          ← Назад
        </Button>
      </p>

      {loading ? <LoadingIndicator label="Загрузка релиза…" /> : null}
      {!loading && error ? <ErrorMessage>{error}</ErrorMessage> : null}

      {collection.conflictMessage ? (
        <ErrorMessage>{collection.conflictMessage}</ErrorMessage>
      ) : null}
      {collection.errorMessage ? (
        <ErrorMessage>{collection.errorMessage}</ErrorMessage>
      ) : null}

      {!loading && release ? (
        <article className="hw-release-detail">
          <ReleaseImageGallery
            release={release}
            selectedImageId={selectedImageId}
            onSelect={setSelectedImageId}
            onReplaceUserImage={(imageId) => startReplace(imageId)}
            onDeleteUserImage={(imageId) => {
              void deleteUserPhoto(imageId);
            }}
          />
          {canUpload ? (
            <ReleasePhotoUpload
              release={release}
              onUploaded={(updated, uploadedImageId) => {
                setRelease(updated);
                if (uploadedImageId) {
                  setSelectedImageId(uploadedImageId);
                }
                catalog.patchRelease(updated);
                collection.patchRelease(updated);
              }}
            />
          ) : null}

          {canUpload ? (
            <div className="hw-user-photo-replace">
              <input
                ref={replaceInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                hidden
                aria-label="Заменить фото"
                onChange={(event) => {
                  const next = event.target.files?.[0] ?? null;
                  if (!next) {
                    clearReplace();
                    return;
                  }
                  if (replacePreviewUrl) {
                    URL.revokeObjectURL(replacePreviewUrl);
                  }
                  setReplaceFile(next);
                  setReplacePreviewUrl(URL.createObjectURL(next));
                }}
              />
              {replaceTargetImageId && replaceFile ? (
                <div className="hw-photo-upload__preview-card">
                  {replacePreviewUrl ? (
                    <img
                      src={replacePreviewUrl}
                      alt="Предпросмотр"
                      className="hw-photo-upload__preview"
                    />
                  ) : (
                    <p className="hw-photo-upload__filename">{replaceFile.name}</p>
                  )}
                  <div className="hw-photo-upload__actions">
                    <Button type="button" onClick={() => void replace()} disabled={replaceBusy}>
                      {replaceBusy ? "Загрузка…" : "Загрузить"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={replaceBusy}
                      onClick={() => clearReplace()}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : null}
              {replaceError ? (
                <p className="hw-photo-upload__error">{replaceError}</p>
              ) : null}
            </div>
          ) : null}
          <ReleaseMeta release={release} headingLevel="h1" />
          <CollectionQuantityControls
            quantity={quantity}
            disabled={busy || mutatingThis || collection.revision === null}
            onAdd={() => {
              void collection.setQuantity(release.releaseId, 1);
            }}
            onIncrement={() => {
              void collection.setQuantity(
                release.releaseId,
                quantity + 1,
              );
            }}
            onDecrement={() => {
              void collection.setQuantity(
                release.releaseId,
                quantity - 1,
              );
            }}
            onRemove={() => {
              void collection.setQuantity(release.releaseId, 0);
            }}
          />
        </article>
      ) : null}
    </section>
  );
}
